import { localDateKey, todayKey } from '../../../utils/datetime';
import React from 'react';
import { Search, ChevronLeft, ChevronRight, Download } from 'lucide-react';
import { useDutyRoster } from './DutyRosterContext';

const RosterHeader = () => {
    const { currentDate, setCurrentDate, weekDates, publishLoading, rosterView, setRosterView, dailyDate, setDailyDate, searchTerm, setSearchTerm, navigate, isDomiciliaryManager, openExportModal, jumpToNow, formatWeekRange, changes, handleDiscard, setPublishModalOpen } = useDutyRoster();
    const pendingCount = changes.length;
    return (
            <div className="sticky top-0 z-30 bg-[#EEF2F6] dark:bg-[#1b1f26] -mx-6 md:-mx-8 px-6 md:px-8 pt-4 -mt-6 md:-mt-8 pb-2.5 border-b border-[#E2E8F0] dark:border-gray-800">
                {/* Header Section */}
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 mb-3">
                    <div className="flex items-center gap-5 flex-wrap">
                        <h1 className="text-[22px] font-semibold text-[#1E293B] dark:text-gray-200 tracking-tight">Schedule</h1>

                        {/* Week Navigation — inline with the title */}
                        <div className="flex items-center gap-3">
                            <button onClick={() => {
                                const newDate = new Date(currentDate);
                                newDate.setDate(newDate.getDate() - 7);
                                setCurrentDate(newDate);
                            }} className="p-1 rounded-md hover:bg-black/5 transition text-[#64748B] dark:text-gray-400">
                                <ChevronLeft className="w-5 h-5" />
                            </button>
                            <span className="text-[14px] font-medium text-[#334155] dark:text-gray-200">
                                Week of {formatWeekRange(weekDates)}
                            </span>
                            <button onClick={() => {
                                const newDate = new Date(currentDate);
                                newDate.setDate(newDate.getDate() + 7);
                                setCurrentDate(newDate);
                            }} className="p-1 rounded-md hover:bg-black/5 transition text-[#64748B] dark:text-gray-400">
                                <ChevronRight className="w-5 h-5" />
                            </button>
                            {(() => {
                                // Show the "Jump to Now" button only when the visible week
                                // doesn't already contain today, so it doesn't add noise when
                                // it would be a no-op.
                                const today = new Date();
                                const todayStr = localDateKey(today);
                                const inCurrentWeek = weekDates.some(d => localDateKey(d) === todayStr);
                                if (inCurrentWeek) return null;
                                return (
                                    <button
                                        onClick={jumpToNow}
                                        className="ml-2 inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl text-[13px] font-bold text-white bg-red-500 hover:bg-red-600 transition shadow-sm"
                                        title="Jump back to the week containing today"
                                    >
                                        <span className="w-1.5 h-1.5 rounded-full bg-white"></span>
                                        Jump to Now
                                    </button>
                                );
                            })()}
                        </div>
                    </div>

                    <div className="flex items-center gap-2.5 flex-wrap">
                        <div className="relative">
                            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8] dark:text-gray-500" />
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Search employee....."
                                className="pl-10 pr-4 py-2 rounded-lg border border-transparent dark:border-gray-700 focus:border-blue-100 bg-white dark:bg-[#31373f] shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500/10 w-[200px] text-[13px] dark:text-gray-200 placeholder:text-[#94A3B8] dark:placeholder:text-gray-500"
                            />
                        </div>
                        {/* Day / Week view toggle — inline toggle replacing the old Filter button */}
                        <div className="flex items-center gap-0.5 bg-white dark:bg-[#31373f] rounded-lg shadow-sm p-0.5">
                            <button
                                onClick={() => setRosterView('daily')}
                                className={`px-3 py-1.5 rounded-md text-[13px] font-bold transition ${
                                    rosterView === 'daily'
                                        ? 'bg-[#2563EB] text-white shadow-sm'
                                        : 'text-[#475569] dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#31373f]'
                                }`}
                            >
                                Day
                            </button>
                            <button
                                onClick={() => setRosterView('weekly')}
                                className={`px-3 py-1.5 rounded-md text-[13px] font-bold transition ${
                                    rosterView === 'weekly'
                                        ? 'bg-[#2563EB] text-white shadow-sm'
                                        : 'text-[#475569] dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#31373f]'
                                }`}
                            >
                                Week
                            </button>
                        </div>
                        {pendingCount > 0 && (
                            <button
                                onClick={handleDiscard}
                                disabled={publishLoading}
                                className="px-3 py-2 rounded-lg border border-[#D5DFEB] dark:border-gray-700 text-[#475569] dark:text-gray-400 font-medium hover:bg-gray-50 dark:hover:bg-[#31373f] transition text-[13px] disabled:opacity-40"
                                title="Discard all unpublished changes"
                            >
                                Discard
                            </button>
                        )}
                        <button
                            onClick={openExportModal}
                            className="bg-white dark:bg-[#31373f] text-[#334155] dark:text-gray-200 border border-[#D5DFEB] dark:border-gray-700 px-4 py-2 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-[#31373f] transition shadow-sm flex items-center gap-2 text-[13px]"
                            title="Export payroll-ready hours as CSV"
                        >
                            <Download className="w-4 h-4" />
                            Export
                        </button>
                        <button
                            onClick={() => setPublishModalOpen(true)}
                            disabled={publishLoading || pendingCount === 0}
                            className="bg-[#2563EB] text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition shadow-sm disabled:bg-blue-400 disabled:cursor-not-allowed flex items-center gap-2 text-[13px]"
                            title={pendingCount === 0 ? 'No unpublished changes' : `Review & publish ${pendingCount} change(s)`}
                        >
                            {publishLoading && <span className="animate-spin mr-2">◌</span>}
                            {pendingCount > 0 ? `Publish (${pendingCount})` : 'Publish'}
                        </button>
                    </div>
                </div>

                {/* Daily view: weekday pills (Mon–Sun) so the manager can pick one
                day inside the visible week; the timeline below renders only that
                day with hour columns. The weekly day-header now lives inside the
                horizontally-scrollable grid below so it stays aligned with the
                team rows when the week is wider than the screen. */}
                {rosterView === 'daily' && (
                    /* Compact day pills — day-name + date in a single chip so the
                       manager doesn't need a separate summary banner to know which
                       day is selected. The active pill also shows the month. */
                    <div className="flex items-center gap-2 px-1 flex-wrap">
                        {weekDates.map(date => {
                            const dStr = localDateKey(date);
                            const selectedStr = localDateKey(dailyDate);
                            const todayStr = todayKey();
                            const isSelected = dStr === selectedStr;
                            const isToday = dStr === todayStr;
                            const shortDay = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][date.getDay()];
                            const shortMonth = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][date.getMonth()];
                            return (
                                <button
                                    key={dStr}
                                    onClick={() => setDailyDate(new Date(date))}
                                    className={`relative flex items-center gap-2 px-3.5 py-2 rounded-xl transition shadow-sm ${isToday
                                            ? `bg-white dark:bg-[#31373f] text-[#475569] dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#31373f]${isSelected ? ' ring-2 ring-[#2563EB]/40' : ''}`
                                            : isSelected
                                                ? 'bg-[#2563EB] text-white'
                                                : 'bg-white dark:bg-[#31373f] text-[#475569] dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#31373f]'
                                        }`}
                                >
                                    <span className={`text-[11px] font-bold uppercase tracking-wider ${!isToday && isSelected ? 'text-blue-100' : 'text-[#94A3B8] dark:text-gray-500'}`}>
                                        {shortDay}
                                    </span>
                                    <span className="text-[14px] font-bold leading-none">{date.getDate()}</span>
                                    {!isToday && isSelected && (
                                        <span className="text-[10px] font-semibold uppercase tracking-wide text-blue-100 ml-0.5">{shortMonth}</span>
                                    )}
                                    {/* Present date is marked by a dot only — no blue fill. */}
                                    {isToday && (
                                        <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-[#2563EB]"></span>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>
    );
};

export default RosterHeader;
